import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import type { ApiClient } from '../api';
import { Geraetetafel } from './Geraetetafel';
import type { Geraetezeile } from './Geraetetafel';
import {
  ContentSection, EmptyState, ErrorState, LoadingState, PropertyGrid,
} from '../muster';

/**
 * Die Geräte der Sprinkleranlage — mit Fabrikatsansicht und öffentlichem Link.
 *
 * --- Was hier ein Gerät ist ------------------------------------------------
 *
 * Drei Arten, und sie sind wirklich verschieden: 12 Alarmventilstationen,
 * 4 Feuerlöschpumpen, 73 Wandhydranten.
 *
 * Dazu die Sprinklerköpfe — aber als TYPZEILE, nicht als Gerät. 5.351 Zeilen
 * mit je einem K-Faktor wären keine Geräteliste, sondern eine Stückliste; für
 * die gibt es den Reiter „Sprinkler". Die Produktdaten des Kopfes hängen
 * ohnehin am Typ und sind an allen 5.351 dieselben, also steht eine Zeile je
 * Typ da und nennt ihre Stückzahl.
 *
 * --- Die Bezeichnung statt des Familiennamens ------------------------------
 *
 * In der Liste steht „Alarmventilstation nass" und nicht
 * „474_PA_AlarmCheckValve_European, DN150". Der Familienname ist der
 * Modellname; außerhalb des Modells sagt er niemandem etwas.
 *
 * --- Der öffentliche Verweis -----------------------------------------------
 *
 * Derselbe Weg wie bei Heizung, Sanitär, Elektro und den Außenanlagen: ein
 * Link mit HMAC-Signatur, der ohne Anmeldung geöffnet werden kann. Er gehört
 * aufs Typenschild — wer vor der Station steht, soll ihre Daten sehen können,
 * ohne ein Konto zu haben.
 */

export function SprinklerGeraete({ api, projektId }: {
  api: ApiClient; projektId: string;
}) {
  const navigieren = useNavigate();
  const [d, setD] = useState<any | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    api.sprinklerGeraete(projektId)
      .then((x) => { setD(x); setFehler(null); })
      .catch((e) => setFehler(e instanceof Error ? e.message : String(e)));
  }, [api, projektId]);

  // Alle drei Arten in EINE Tafel — das Muster der Heizung.
  const zeilen: Geraetezeile[] = useMemo(() => {
    if (!d) return [];

    const aus: Geraetezeile[] = [];

    // ════════════════════════════════════════════════════════════════════
    // DIE SPRINKLERKÖPFE — EINE ZEILE JE TYP
    // ════════════════════════════════════════════════════════════════════
    //
    // 5.351 Köpfe sind keine Geräteliste, sondern eine Stückliste. Deshalb
    // standen sie hier bisher gar nicht.
    //
    // Ihre PRODUKTDATEN brauchen trotzdem einen Ort: K-Faktor,
    // Auslösetemperatur, VdS-Nummer. Die hängen am Typ und sind an allen
    // 5.351 Köpfen dieselben — eine Zeile je Typ ist also nicht die
    // Abkürzung, sondern die richtige Auflösung.
    //
    // Sie stehen ZUERST: Der Sprinkler ist das Bauteil, um das es geht.
    for (const k of d.kopftypen ?? [])
      aus.push({
        id: k.id, art: 'Sprinkler',
        bezeichnung: k.bezeichnung, familie: k.familie, typ: k.typ,
        kennung: null, raum: null, geschoss: null,

        // Die Stückzahl als Kennwert: Sie sagt, für wie viele Köpfe diese
        // eine Zeile gilt — sonst sähe sie aus wie ein einzelner Sprinkler.
        kennwert: `${(k.anzahl ?? 0).toLocaleString('de-DE')} Stück`,
        verweis: k.verweis,
        offen: null,
      });

    for (const s of d.stationen ?? [])
      aus.push({
        id: s.id, art: 'Alarmventilstation',
        bezeichnung: s.bezeichnung, familie: s.familie, typ: s.typ,
        kennung: s.kennung, raum: s.raumnummer, geschoss: s.geschoss,
        kennwert: s.nennweite ? `DN${s.nennweite}` : null,
        verweis: s.verweis,
        offen: s.raumnummer ? null : ['Raumzuordnung fehlt'],
      });

    for (const p of d.pumpen ?? [])
      aus.push({
        id: p.id, art: 'Feuerlöschpumpe',
        bezeichnung: p.bezeichnung, hersteller: p.hersteller,
        modell: p.modell, familie: p.familie, typ: p.typ,
        kennung: p.kennung, raum: p.raumnummer, geschoss: p.geschoss,
        kennwert: p.foerderstromLMin
          ? `${p.foerderstromLMin} l/min` : null,
        verweis: p.verweis,
        offen: [
          p.raumnummer ? null : 'Raumzuordnung fehlt',
          p.hersteller ? null : 'Fabrikat fehlt',
          p.foerderstromLMin ? null : 'Förderstrom fehlt',
        ].filter(Boolean) as string[],
      });

    for (const h of d.hydranten ?? [])
      aus.push({
        id: h.id, art: 'Wandhydrant',
        bezeichnung: h.bezeichnung, familie: h.familie, typ: h.typ,
        kennung: h.kennung,
        raum: [h.raumnummer, h.raumname].filter(Boolean).join(' '),
        geschoss: h.geschoss,
        kennwert: h.druckbedarfBar ? `${h.druckbedarfBar} bar` : null,
        verweis: h.verweis,
        offen: [
          h.raumnummer ? null : 'Raumzuordnung fehlt',
          h.kennung ? null : 'Kennzeichen fehlt',
        ].filter(Boolean) as string[],
      });

    return aus;
  }, [d]);

  if (fehler) {
    return (
      <ErrorState
        meldung="Die Geräte der Sprinkleranlage konnten nicht geladen werden."
        technisch={fehler}
        beiErneutVersuchen={() => {
          setFehler(null);
          api.sprinklerGeraete(projektId)
            .then((x) => { setD(x); setFehler(null); })
            .catch((e) => setFehler(e instanceof Error ? e.message : String(e)));
        }}
      />
    );
  }

  if (!d) return <LoadingState text="Sprinklergeräte werden geladen …" zeilen={8} spalten={5} />;

  // Der Detailverweis geht auf die PRODUKTSEITE — dieselbe wie bei der
  // Heizung. Vorher öffnete sich eine eigene Karte unter der Tabelle; das
  // war eine zweite Darstellung derselben Sache.
  return (
    <Geraetetafel
      zeilen={zeilen}
      kennwertName="Kennwert"
      aufDetail={(id) => navigieren(`../sprinkler-geraete/${id}`)}
    />
  );
}

/**
 * Dieselbe Ansicht ohne Anmeldung, über den signierten Verweis.
 *
 * Sie zeigt NUR, was gesichert ist. Die offenen Punkte bleiben der internen
 * Ansicht vorbehalten: Auf der Baustelle nützt niemandem, was noch fehlt —
 * und eine öffentliche Mängelliste wäre eine Aussage über den Planungsstand
 * an Leute, die sie nicht einordnen können.
 */
export function SprinklergeraetOeffentlich() {
  const { id } = useParams();
  const [suche] = useSearchParams();
  const [d, setD] = useState<any | null>(null);
  const [fehler, setFehler] = useState(false);

  useEffect(() => {
    const s = suche.get('s');

    if (!id || !s) { setFehler(true); return; }

    fetch(`/api/oeffentlich/sprinklergeraet/${id}?s=${encodeURIComponent(s)}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setD)
      .catch(() => setFehler(true));
  }, [id, suche]);

  if (fehler) {
    return (
      <div style={{ padding: 32, maxWidth: 560, margin: '0 auto' }}>
        <EmptyState
          titel="Nicht gefunden"
          erklaerung="Dieser Verweis ist ungültig oder das Gerät gibt es nicht mehr."
        />
      </div>
    );
  }

  if (!d) return <LoadingState text="Gerätedaten werden geladen …" zeilen={6} />;

  return (
    <div style={{ padding: 24, maxWidth: 620, margin: '0 auto' }}>
      <ContentSection
        titel={d.bezeichnung}
        beschreibung={d.kennung ?? `Id ${d.revitElementId}`}
        meta={`${d.familie} · ${d.typ}`}
      >
        <PropertyGrid
          spalten={1}
          eintraege={(d.werte ?? []).map((w: any) => ({
            beschriftung: w.name,
            wert: w.wert,
            herkunft: 'daten' as const,
          }))}
        />
      </ContentSection>
    </div>
  );
}
